<template>
  <div class="app-body profile d-flex justify-content-center" ref="appbody">
    <div class="profile-form" v-if="this.ready && !this.networkError">
      <h1 class="mt-4">{{ newRegistration ? 'Create your user profile' : 'Your user profile' }}</h1>
      <form autocomplete="off">
        <label for="name">Name</label>
        <input id="name" class="mb-2 mx-auto" v-model="name" placeholder="Name" autocomplete="off">
        <label for="email">Email</label>
        <input
          id="email"
          class="mb-2 mx-auto text-lowercase"
          v-model="email"
          placeholder="Email address"
          autocomplete="off"
          autocapitalize="off"
          inputmode="email"
          v-bind:disabled="!newRegistration"
        >
        <label for="password">Password</label>
        <input
          id="password"
          class="mb-2 mx-auto"
          v-model="password"
          v-bind:placeholder="passwordPlaceholder"
          type="password"
          autocomplete="new-password"
        >
        <label for="name">Year of birth</label>
        <input
          id="yearOfBirth"
          class="mb-2 mx-auto"
          v-model="yearOfBirth"
          placeholder="Year of birth"
          type="number"
        >
        <label for="name">Gender</label>
        <div class="select-container">
          <select id="gender" class="custom-select" v-model="gender">
            <option value>Select your gender...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other/prefer not to say</option>
          </select>
        </div>
      </form>
      <p class="validation-error mt-2 mb-2" v-if="showError">{{errorMessage}}</p>
      <div class="contact d-flex justify-content-center mb-3 pl-2 pr-2">
        <span v-if="showError">
          <span>Drop us a line at</span>
          <a href="mailto:support@aftv.co.uk" target="_blank">support@aftv.co.uk</a>
          <span>if you have problems with sign-in or registration</span>
        </span>
      </div>
      <div class="d-flex flex-column">
        <button class="primary ml-0 mr-0" @click="onCreate" v-if="this.newRegistration">Create</button>
        <button class="primary ml-0 mr-0" @click="onUpdate" v-if="!this.newRegistration">Update</button>
        <button class="secondary ml-0 mr-0" @click="onDelete" v-if="!this.newRegistration">Delete my account</button>
      </div>
    </div>
    <div id="networkError" class="network-error d-flex flex-column align-items-center" v-if="networkError">
      <span>Please check your network connection and try again.</span>
      <button class="primary ml-0 mt-3 mb-4" @click="loadProfile">Retry</button>
    </div>
  </div>
</template>

<script>
import { spinnakerApi } from '../js/SpinnakerApi.js'

const missingCredential = 'Please input valid username and password.'

export default {
  components: {},
  name: 'Profile',
  data: function () {
    return {
      profile: null,
      name: '',
      email: '',
      password: '',
      yearOfBirth: '',
      gender: '',
      ready: false,
      showError: false,
      errorMessage: missingCredential,
      passwordPlaceholder: '',
      networkError: false
    }
  },
  props: ['newRegistration'],
  created: async function () {
    await this.loadProfile()
  },
  methods: {
    async loadProfile () {
      if (this.newRegistration) {
        this.passwordPlaceholder = 'Mix of letters & numbers (8 chars min)'
        this.ready = true
        return
      }

      this.$store.commit('showLoading')
      this.ready = false
      this.networkError = false

      let accountSummary = null
      try {
        accountSummary = await spinnakerApi.fetchAccountSummary()
      } catch (err) {
        this.errorMessage = 'Failed to load account summary.'
        this.$store.commit('hideLoading')
        this.networkError = true
        return
      }

      if (accountSummary.account.type === 'Registered') {
        this.profile = await spinnakerApi.fetchProfile()
        this.passwordPlaceholder = 'Password (blank for no change)'

        if (this.profile && this.profile.email) {
          this.name = this.profile.fullName
          this.email = this.profile.email
          this.yearOfBirth = this.profile.dateOfBirth
          this.gender = this.profile.gender
        }

        switch (accountSummary.account.accountStatus) {
          case 'Active':
            // do nothing
            break
          case 'ConfirmationPending':
            this.errorMessage =
              'Please open the link in your confirmation email to activate your account.'
            this.showError = true
            break
          default:
            this.errorMessage = 'This account has been disabled.'
            this.showError = true
            break
        }

        this.$store.commit('hideLoading')
        this.ready = true
      } else {
        location.href = location.href.replace(location.pathname, '')
      }
    },
    async onCreate () {
      if (!this.validate()) {
        return
      }

      this.$store.commit('showLoading')

      try {
        await spinnakerApi.registerProfile(
          this.getEmail(),
          this.password,
          this.name,
          this.yearOfBirth,
          this.gender
        )
        await spinnakerApi.switchToCasualUser()
        this.goBack()

        this.$store.commit('showDialogue', {
          title: 'Registration successful!',
          subtitle: 'Please check your email and follow the activation link.'
        })
      } catch (err) {
        this.errorMessage = this.errorResponseToDescription(err)
        this.showError = true
      }

      this.$store.commit('hideLoading')
    },
    async onUpdate () {
      if (!this.validate()) {
        return
      }
      this.profile.fullName = this.name
      this.profile.email = this.getEmail()
      this.profile.dateOfBirth = this.yearOfBirth
      this.profile.gender = this.gender
      this.profile.password = this.password

      this.$store.commit('showLoading')

      try {
        await spinnakerApi.updateProfile(this.profile)
        this.goBack()

        this.$store.commit('showDialogue', {
          title: 'Profile updated!',
          subtitle: 'Your changes have been saved.'
        })
      } catch (err) {
        this.errorMessage = this.errorResponseToDescription(err)
        this.showError = true
      }

      this.$store.commit('hideLoading')
    },
    onDelete () {
      this.$store.commit('showDeleteAccount')
      this.goBack()
    },
    getEmail () {
      if (this.email) {
        return this.email.toLowerCase().trim()
      }
    },
    validate () {
      this.errorMessage = null
      if (this.name.trim().length === 0) {
        this.errorMessage = 'Please enter your name.'
      } else if (
        this.email.trim().length === 0 ||
        this.email.indexOf('@') === -1
      ) {
        this.errorMessage = 'Please enter a valid email address.'
      } else if ((this.newRegistration && !this.isPasswordValid()) ||
          (!this.newRegistration && this.isPasswordSpecified() && !this.isPasswordValid())) {
        this.errorMessage = 'Password must contain a mix of letters and numbers ' +
          'and be 8 or more characters in length.'
      } else if (typeof this.yearOfBirth === 'undefined' || this.yearOfBirth.trim().length === 0) {
        this.errorMessage = 'Please enter a valid birth year.'
      } else if (typeof this.gender === 'undefined' || this.gender.trim().length === 0) {
        this.errorMessage = 'Please select your gender.'
      }

      this.showError = this.errorMessage !== null
      return !this.showError
    },
    isPasswordSpecified () {
      return typeof this.password !== 'undefined' && this.password.length > 0
    },
    isPasswordValid () {
      if (typeof this.password === 'undefined') {
        return false
      }
      if (this.password.length < 8) {
        return false
      }
      if (!/\d/.test(this.password)) {
        return false
      }
      if (!/[a-zA-Z]/.test(this.password)) {
        return false
      }
      return true
    },
    errorResponseToDescription (err) {
      let response = err.response
      let errorMessage = `An unexpected error occurred${
        response && response.status ? ' [' + response.status + ']' : ''
      }`
      if (
        response &&
        response.status === 400 &&
        response.data &&
        response.data.message
      ) {
        if (response.data.errorCode === 225) {
          errorMessage = 'Email address already registered'
        } else if (
          response.data.errorCode === 225 &&
          response.data.message.indexOf('date of birth') > -1
        ) {
          errorMessage = 'Invalid year of birth'
        } else {
          errorMessage = response.data.message
        }
      }
      return errorMessage
    },
    goBack () {
      this.$store.commit('navigation/goBack')
    }
  }
}
</script>
